import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import { LogOut, TrendingUp, CheckCircle2, Clock3, ArrowUpRight, ShieldCheck } from "lucide-react";
import NewsletterPanel from "@/components/NewsletterPanel";

const KPI = ({ id, label, value, delta }) => (
  <div className="border border-ink-200 p-6 bg-white" data-testid={`kpi-${id}`}>
    <p className="eyebrow text-ink-500">{label}</p>
    <div className="mt-4 flex items-baseline justify-between gap-4">
      <p className="font-serif text-3xl md:text-4xl text-teal-dark leading-none">{value}</p>
      {delta && delta !== "—" && (
        <span className="text-xs eyebrow bg-teal-l1 text-teal-dark px-2 py-1">{delta}</span>
      )}
    </div>
  </div>
);

const DashboardPage = () => {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (user === false) nav("/login");
  }, [user, nav]);

  useEffect(() => {
    if (!user || user === false) return;
    api.get("/dashboard/summary").then((r) => setData(r.data)).catch((e) => setErr("Impossible de charger le tableau de bord."));
  }, [user]);

  if (user === null) return <div className="min-h-screen flex items-center justify-center text-ink-500">Chargement…</div>;
  if (user === false) return null;

  return (
    <div data-testid="dashboard-page" className="min-h-screen bg-teal-l1/40">
      <div className="bg-teal-dark text-white grain relative overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-10 flex flex-wrap justify-between items-end gap-6">
          <div>
            <p className="eyebrow text-teal-l3">Espace {user.role === "admin" ? "administrateur" : "client"} · Exercice {data?.exercise_year || 2026}</p>
            <h1 className="mt-3 font-serif text-4xl md:text-5xl leading-tight font-light">
              Bonjour <em className="text-teal-l3">{user.name?.split(" ")[0] || "client"}</em>
            </h1>
            <p className="mt-2 text-teal-l3 text-sm flex items-center gap-2">
              {user.company || data?.company}
              {user.role === "admin" && (
                <span className="inline-flex items-center gap-1 border border-white/40 px-2 py-0.5 text-xs eyebrow">
                  <ShieldCheck size={12} /> Admin
                </span>
              )}
            </p>
          </div>
          <div className="flex gap-3">
            <Link to="/" className="text-sm border border-white/40 px-4 py-2 hover:bg-white hover:text-teal-dark transition-colors" data-testid="dashboard-home-link">
              Retour au site
            </Link>
            <button onClick={logout} data-testid="dashboard-logout" className="text-sm inline-flex items-center gap-2 border border-white/40 px-4 py-2 hover:bg-white hover:text-teal-dark transition-colors">
              <LogOut size={14} /> Déconnexion
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-10">
        {err && <div className="mb-6 border-l-4 border-red-500 bg-red-50 text-red-800 px-4 py-3 text-sm">{err}</div>}
        {!data ? (
          <div className="text-ink-500">Chargement des données…</div>
        ) : (
          <>
            {data.is_admin && data.kpis && (
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                {Object.entries(data.kpis).map(([k, v]) => (
                  <KPI key={k} id={k} label={v.label} value={v.value} delta={v.delta} />
                ))}
              </div>
            )}

            <div className={`${data.is_admin && data.kpis ? "mt-8" : ""} grid lg:grid-cols-3 gap-4`}>
              {data.is_admin && data.monthly_revenue && (
                <div className="lg:col-span-2 border border-ink-200 bg-white p-6" data-testid="revenue-chart">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="eyebrow text-ink-500">Évolution du chiffre d'affaires mensuel</p>
                      <p className="font-serif text-2xl text-teal-dark mt-1">Exercice {data.exercise_year}</p>
                    </div>
                    <TrendingUp size={22} className="text-teal-accent" />
                  </div>
                  <div className="mt-6 h-72">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={data.monthly_revenue} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                        <XAxis dataKey="month" tick={{ fill: "#64748B", fontSize: 12 }} axisLine={{ stroke: "#E2E8F0" }} tickLine={false} />
                        <YAxis tick={{ fill: "#64748B", fontSize: 12 }} axisLine={{ stroke: "#E2E8F0" }} tickLine={false} tickFormatter={(v) => `${v / 1000}k`} />
                        <Tooltip
                          contentStyle={{ background: "#0B3D3A", border: "none", color: "white" }}
                          labelStyle={{ color: "#BEEAE3" }}
                          formatter={(v) => [`${v.toLocaleString("fr-FR")} €`, "CA"]}
                        />
                        <Line type="monotone" dataKey="value" stroke="#0B3D3A" strokeWidth={2.5} dot={{ fill: "#00897B", r: 4 }} activeDot={{ r: 6, fill: "#00897B" }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              <div className={`border border-ink-200 bg-white p-6 ${!(data.is_admin && data.monthly_revenue) ? "lg:col-span-3" : ""}`} data-testid="statuses-panel">
                <p className="eyebrow text-ink-500">Statuts en cours</p>
                <p className="font-serif text-2xl text-teal-dark mt-1">Vos échéances</p>
                <ul className={`mt-6 ${!(data.is_admin && data.monthly_revenue) ? "grid md:grid-cols-2 gap-x-10 gap-y-4" : "space-y-4"}`}>
                  {data.statuses.map((s) => (
                    <li key={s.label} className="flex gap-3 items-start border-b border-ink-200 pb-3 last:border-0">
                      {s.status === "done" ? (
                        <CheckCircle2 size={18} className="text-teal-accent shrink-0 mt-0.5" />
                      ) : (
                        <Clock3 size={18} className="text-yellow-600 shrink-0 mt-0.5" />
                      )}
                      <div>
                        <p className="text-sm text-ink-800">{s.label}</p>
                        <p className="text-xs text-ink-500 mt-0.5">{s.date}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="mt-8 space-y-8" data-testid="documents-panel">
              <NewsletterPanel isAdmin={user.role === "admin"} />
              {user.role === "admin" && (
                <NewsletterPanel
                  isAdmin={true}
                  board="recruitment"
                  listUrl="/recruitment/documents"
                  categories={["Offres d'emploi", "Nos valeurs", "Avantages", "Processus de recrutement", "Général"]}
                  eyebrow="Documents recrutement"
                  title="Espace recrutement"
                  description="Documents visibles publiquement sur la page /recrutement (fiches de poste, avantages, processus, etc.)."
                  emptyTitle="Aucune publication de recrutement"
                  addLabel="Ajouter un document de recrutement"
                  testIdPrefix="recruitment-admin"
                />
              )}
            </div>

            <div className="mt-10 border border-teal-dark bg-teal-dark text-white p-8 md:p-10 flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
              <div>
                <p className="eyebrow text-teal-l3">Besoin d'un échange ?</p>
                <p className="font-serif text-2xl md:text-3xl mt-2">Contactez votre interlocuteur dédié</p>
              </div>
              <Link to="/contact" data-testid="dashboard-contact-cta" className="inline-flex items-center gap-2 bg-white text-teal-dark px-6 py-3 hover:bg-teal-l1 transition-colors">
                Prendre rendez-vous <ArrowUpRight size={16} />
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DashboardPage;
